import { FormControl, UntypedFormGroup } from "@angular/forms";
import { HttpClient } from "@angular/common/http";
import LayerUtils from "@vision/core-map-api/src/utils/LayerUtils";
import { Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { GeoJSONDatabaseService } from "../../viewer-components/services/internal-services/geojson-database.service";
import { BaseViewerService } from "../../viewer-components/services/internal-services/viewer-services/base-viewer.service";
import { IDialogData } from "../../viewer-components/interfaces/i-dialog-data";
import { ViewerConfigurationService } from "../../viewer-components/services/viewer-configuration.service";
import { Viewerlayer } from "../../viewer-components/model/viewerlayer";
import { ILayerStyleVector } from "../../viewer-components/interfaces/i-layer-style-vector";
import { ViewerConstants } from "../../shared/model/viewer-constants";
import { GoogleAnalyticsService } from "../../shared/services/google-analytics.service";

export abstract class ArcGISLayerResultsDialogComponent {
  serviceTypes = [
    "FeatureServer",
    "GeocodeServer",
    "GeoDataServer",
    "GeometryServer",
    "GPServer",
    "GlobeServer",
    "ImageServer",
    "MapServer",
    "MobileServer",
    "SceneServer",
    "SchematicsServer",
    "StreamServer",
    "TopographicProductionServer",
    "NetworkDiagramServer",
    "ValidationServer",
    "VectorTileServer",
    "VersionManagementServer",
  ];

  public viewerService: BaseViewerService;

  layers: any[] = [];

  tilesMatrixSet: any[] = [];

  matrixConfiguration: any;

  hide = true;

  isLoading = true;

  formatPercentLabel = (value: number) => `${value}%`;

  isAuthRequired: boolean;

  showEditIcon: boolean = false;

  url = "";

  token: any;

  useBbox: boolean = false;

  password: string;

  username: string;

  static FEATURE_SERVER = "FeatureServer";

  newFeatureLayerForm = new UntypedFormGroup({
    newFeatureLayerName: new FormControl(""),
    newFeatureLayerOpacity: new FormControl(100),
  });

  protected constructor(
    protected http: HttpClient,
    protected viewerConfigurationService: ViewerConfigurationService,
    protected geoJSONDatabaseService: GeoJSONDatabaseService,
    public googleAnalyticsService: GoogleAnalyticsService,
    protected matDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: IDialogData,
  ) {
    this.googleAnalyticsService = googleAnalyticsService;
    this.url = data.url;
    this.username = data.username;
    this.password = data.password;
    this.token = data.token;
    this.isAuthRequired = data.isAuthRequired;
    this.viewerService = data.viewerService;
    this.isLoading = true;
    this.useBbox = data.useBbox;
    this.layers = data.layers;
    for (let index = 0; index < this.layers.length; index++) {
      this.newFeatureLayerForm.addControl(
        `newFeatureLayerName-${index}`,
        new FormControl(`${this.layers[index].name}`),
      );
    }
  }

  getTypeServer(url): string {
    const urlArray = url.split("/").filter((s) => s);
    const urlType = urlArray[urlArray.length - 1];
    return this.serviceTypes.includes(urlType) ? urlType : null;
  }

  anyChecked(): boolean {
    if (this.layers) {
      return this.layers.some((feature) => feature.checked !== undefined && feature.checked);
    }
    return false;
  }

  addArcGISFeatureLayers() {
    let { url } = this;

    url = LayerUtils.removeParam("request", url);

    let extraParams;
    const layerIds = [];
    this.layers
      .filter((layer) => layer.checked)
      .map((layer) => {
        layerIds.push(layer.id);
        return layer;
      });

    if (this.username.length > 0 && this.password.length > 0) {
      extraParams = {
        URL_ORIGIN: url,
        USERNAME: this.username,
        PASSWORD: this.password,
        TOKEN: this.token,
      };
    }

    const layersText = layerIds.join(",");
    let name = this.newFeatureLayerForm.get("newFeatureLayerName").value;
    let opacity = this.newFeatureLayerForm.get("newFeatureLayerOpacity").value;
    if (name.length === 0) {
      name = "Custom ArcGIS Feature Layer";
    }
    if (opacity === "") {
      opacity = 1;
    } else {
      opacity /= 100;
    }
    const arcgisFeatureStyle: ILayerStyleVector = {
      defaultSetting: {
        opacity: opacity.toString(),
        colorFill: `rgba(49,159,211,${opacity})`,
        colorFillOpacity: opacity.toString(),
        colorOutline: "rgba(49,159,211,1)",
        colorOutlineOpacity: "1",
        outlineWidth: "1",
      },
    };
    const layer = {
      source: "ARCGISFEATURE",
      url,
      extraParams,
      removable: true,
      owner: true,
      editable: true,
      layer: layersText,
      isBase: false,
      visible: true,
      realId: `${name.replace(/ /g, "")}_${LayerUtils.layerGuid()}`,
      id: `${name}_${LayerUtils.layerGuid()}`,
      name,
      panelInclude: true,
      style: JSON.stringify(arcgisFeatureStyle),
      order: ViewerConstants.USER_LAYER_BASE_ZINDEX,
      userId: this.viewerConfigurationService.getViewerConfiguration().userId,
      useBbox: this.useBbox,
    };

    const layerToSave = LayerUtils.processLayer(layer) as unknown as Viewerlayer;
    this.saveLayer(layerToSave);

    this.matDialog.closeAll();
  }

  changeCheckLayer(checked: boolean, layer: any) {
    layer.checked = checked;
  }

  editLayerName(layer: any) {
    if (layer.edit) {
      layer.edit = false;
    } else {
      layer.edit = true;
    }
  }

  showHideEditIcon() {
    if (this.showEditIcon) {
      this.showEditIcon = false;
    } else {
      this.showEditIcon = true;
    }
  }

  showDoneIcon(layer: any, index) {
    const featureId = `newFeatureLayerName-${index}`;
    return layer.edit && this.newFeatureLayerForm.get(featureId).value.length > 0;
  }

  abstract saveLayer(layer: Viewerlayer);
}
